
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, UserCheck, Clock, AlertCircle } from 'lucide-react';
import { useUsers } from '@/hooks/useUsers';
import CreateUserModal from './CreateUserModal';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const AdminUsersTab = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const { data: admins = [], isLoading, refetch } = useUsers('super_admin');

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <UserCheck className="w-4 h-4 text-green-500" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'expired':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Registrado';
      case 'pending':
        return 'Pendiente';
      case 'expired':
        return 'Vencido';
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="text-gray-500">Cargando administradores...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Administradores</h2>
          <p className="text-gray-600">Gestiona los usuarios administradores del sistema</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Crear Administrador
        </Button>
      </div>

      <div className="grid gap-4">
        {admins.map((admin) => (
          <Card key={admin.id}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3">
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {admin.first_name} {admin.last_name}
                      </h3>
                      <p className="text-sm text-gray-600">{admin.user_id}</p>
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">Teléfono:</span>
                      <span className="ml-2 text-gray-600">{admin.phone || 'No especificado'}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Creado:</span>
                      <span className="ml-2 text-gray-600">
                        {format(new Date(admin.created_at), 'dd/MM/yyyy', { locale: es })}
                      </span>
                    </div>
                    <div className="flex items-center">
                      {getStatusIcon(admin.status)}
                      <span className="ml-2 text-gray-700">{getStatusText(admin.status)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {admins.length === 0 && (
          <Card>
            <CardContent className="p-8 text-center">
              <p className="text-gray-600">No hay administradores registrados</p>
            </CardContent>
          </Card>
        )}
      </div>

      <CreateUserModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          refetch();
        }}
        userRole="super_admin"
      />
    </div>
  );
};

export default AdminUsersTab;
